import React, { useState } from 'react';
import { HeroSection } from '../components/HeroSection';
import { Button } from '../components/ui/Button';
import { Mail, MessageCircle, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
export function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    queryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const queryTypes = [{
    value: '',
    label: 'Select a topic...'
  }, {
    value: 'courses',
    label: 'Course Enrollment & Questions'
  }, {
    value: 'technical',
    label: 'Technical Support'
  }, {
    value: 'accessibility',
    label: 'Accessibility Issue'
  }, {
    value: 'partnership',
    label: 'Partnership Inquiry'
  }, {
    value: 'press',
    label: 'Press & Media'
  }, {
    value: 'feedback',
    label: 'General Feedback'
  }, {
    value: 'other',
    label: 'Other'
  }];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        queryType: '',
        message: ''
      });
    }, 3000);
  };
  const contactMethods = [{
    icon: <Mail className="h-6 w-6" data-id="element-1104" />,
    title: 'Email',
    details: 'hello@regrowth.com',
    description: 'We respond within 2 business days',
    color: 'border-brand-magenta'
  }, {
    icon: <MessageCircle className="h-6 w-6" data-id="element-1105" />,
    title: 'Community',
    details: 'Join our Discord',
    description: 'Connect with our community',
    color: 'border-brand-cyan'
  }, {
    icon: <Phone className="h-6 w-6" data-id="element-1106" />,
    title: 'Office Hours',
    details: 'Tuesdays, 2-4pm PT',
    description: 'Drop-in virtual office hours',
    color: 'border-brand-orange'
  }, {
    icon: <MapPin className="h-6 w-6" data-id="element-1107" />,
    title: 'Location',
    details: 'Oakland, CA',
    description: 'Remote-first organization',
    color: 'border-brand-lime'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1108">
      <HeroSection title="Get in Touch" subtitle="We'd love to hear from you. Send us a message and we'll respond as soon as possible." tag="Contact Us" bgColor="magenta" data-id="element-1109" />

      <div className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1110">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1111">
          {/* Contact Methods - Left Column */}
          <div className="lg:col-span-1" data-id="element-1112">
            <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-1113">
              Other Ways to Reach Us
            </h2>

            <div className="space-y-6" data-id="element-1114">
              {contactMethods.map((method, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className={`border-l-4 ${method.color} bg-white p-6`} data-id="element-1115">
                  <div className="flex items-start gap-4" data-id="element-1116">
                    <div className="text-brand-black" data-id="element-1117">{method.icon}</div>
                    <div data-id="element-1118">
                      <h3 className="font-bold text-lg mb-1 text-brand-black" data-id="element-1119">
                        {method.title}
                      </h3>
                      <p className="text-brand-black font-medium mb-1" data-id="element-1120">
                        {method.details}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-1121">
                        {method.description}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* FAQ Link */}
            <div className="mt-8 bg-brand-magenta/10 border-2 border-brand-magenta p-6" data-id="element-1122">
              <h3 className="font-bold text-lg mb-2 text-brand-black" data-id="element-1123">
                Looking for Quick Answers?
              </h3>
              <p className="text-gray-700 text-sm mb-4" data-id="element-1124">
                Check our FAQ page for answers to common questions about
                courses, enrollment, and policies.
              </p>
              <a href="/faq" className="text-brand-magenta font-bold text-sm hover:underline" data-id="element-1125">
                Visit FAQ →
              </a>
            </div>
          </div>

          {/* Contact Form - Right Column */}
          <div className="lg:col-span-2" data-id="element-1126">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white border-2 border-brand-black p-8 md:p-12" data-id="element-1127">
              <h2 className="text-3xl font-black uppercase mb-2 text-brand-black" data-id="element-1128">
                Send Us a Message
              </h2>
              <p className="text-gray-600 mb-8" data-id="element-1129">
                Fill out the form below and we'll get back to you within 2
                business days.
              </p>

              {isSubmitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="py-16 text-center" data-id="element-1130">
                  <div className="w-20 h-20 bg-brand-lime rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1131">
                    <CheckCircle className="h-10 w-10 text-white" data-id="element-1132" />
                  </div>
                  <h3 className="text-2xl font-black mb-3 text-brand-black" data-id="element-1133">
                    Message Sent!
                  </h3>
                  <p className="text-gray-700" data-id="element-1134">
                    Thanks for reaching out. We'll respond to your message soon.
                  </p>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1135">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1136">
                    <div data-id="element-1137">
                      <label htmlFor="firstName" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-1138">
                        First Name <span className="text-brand-magenta" data-id="element-1139">*</span>
                      </label>
                      <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors" placeholder="Jane" data-id="element-1140" />
                    </div>

                    <div data-id="element-1141">
                      <label htmlFor="lastName" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-1142">
                        Last Name <span className="text-brand-magenta" data-id="element-1143">*</span>
                      </label>
                      <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors" placeholder="Doe" data-id="element-1144" />
                    </div>
                  </div>

                  {/* Email */}
                  <div data-id="element-1145">
                    <label htmlFor="email" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-1146">
                      Email Address{' '}
                      <span className="text-brand-magenta" data-id="element-1147">*</span>
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors" placeholder="jane@example.com" data-id="element-1148" />
                  </div>

                  {/* Query Type Dropdown */}
                  <div data-id="element-1149">
                    <label htmlFor="queryType" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-1150">
                      What is this about?{' '}
                      <span className="text-brand-magenta" data-id="element-1151">*</span>
                    </label>
                    <select id="queryType" name="queryType" required value={formData.queryType} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors bg-white" data-id="element-1152">
                      {queryTypes.map(type => <option key={type.value} value={type.value} data-id="element-1153">
                          {type.label}
                        </option>)}
                    </select>
                  </div>

                  {/* Message */}
                  <div data-id="element-1154">
                    <label htmlFor="message" className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2" data-id="element-1155">
                      Message <span className="text-brand-magenta" data-id="element-1156">*</span>
                    </label>
                    <textarea id="message" name="message" required value={formData.message} onChange={handleChange} rows={6} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-brand-magenta focus:outline-none transition-colors resize-none" placeholder="Tell us what's on your mind..." data-id="element-1157" />
                    <p className="text-xs text-gray-500 mt-2" data-id="element-1158">
                      Please be as detailed as possible so we can help you
                      better.
                    </p>
                  </div>

                  {/* Privacy Notice */}
                  <div className="bg-gray-50 border-l-4 border-brand-magenta p-4" data-id="element-1159">
                    <p className="text-xs text-gray-600" data-id="element-1160">
                      By submitting this form, you agree to our{' '}
                      <a href="/policies/privacy" className="text-brand-magenta font-bold hover:underline" data-id="element-1161">
                        Privacy Policy
                      </a>
                      . We'll only use your information to respond to your
                      inquiry.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="flex items-center justify-between pt-4" data-id="element-1162">
                    <p className="text-sm text-gray-600" data-id="element-1163">
                      <span className="text-brand-magenta" data-id="element-1164">*</span> Required
                      fields
                    </p>
                    <Button type="submit" color="magenta" size="lg" disabled={isSubmitting} className="min-w-[200px]" data-id="element-1165">
                      {isSubmitting ? <span className="flex items-center gap-2" data-id="element-1166">
                          <span className="animate-spin" data-id="element-1167">⏳</span>
                          Sending...
                        </span> : <span className="flex items-center gap-2" data-id="element-1168">
                          Send Message
                          <Send className="h-4 w-4" data-id="element-1169" />
                        </span>}
                    </Button>
                  </div>
                </form>}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Response Time Notice */}
      <section className="py-16 bg-brand-navy text-white" data-id="element-1170">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1171">
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-1172">What to Expect</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1173">
            <div data-id="element-1174">
              <div className="text-4xl font-black text-brand-magenta mb-2" data-id="element-1175">
                2
              </div>
              <p className="text-sm uppercase tracking-wide text-gray-300" data-id="element-1176">
                Business Days
              </p>
              <p className="text-xs text-gray-400 mt-2" data-id="element-1177">
                Average response time
              </p>
            </div>
            <div data-id="element-1178">
              <div className="text-4xl font-black text-brand-cyan mb-2" data-id="element-1179">24</div>
              <p className="text-sm uppercase tracking-wide text-gray-300" data-id="element-1180">
                Hours
              </p>
              <p className="text-xs text-gray-400 mt-2" data-id="element-1181">For urgent issues</p>
            </div>
            <div data-id="element-1182">
              <div className="text-4xl font-black text-brand-lime mb-2" data-id="element-1183">
                100%
              </div>
              <p className="text-sm uppercase tracking-wide text-gray-300" data-id="element-1184">
                Response Rate
              </p>
              <p className="text-xs text-gray-400 mt-2" data-id="element-1185">We reply to everyone</p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}